import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as fabric from "fabric";

const MAX_CANVAS_WIDTH = 880;
const FREE_DRAW_STROKE_WIDTH = 4;
const SHAPE_STROKE_WIDTH = 3;
const ARROWHEAD_SIZE = 14;
const DRAG_SHAPE_TOOLS = ["rect", "highlight", "circle", "crop", "arrow", "line"];

// Encapsulates all Fabric.js canvas setup, drawing-tool interactions, crop,
// and annotation helpers used by CaptureEditor. Keeping this logic in its
// own hook keeps the page component focused on layout/form concerns.
export function useAnnotationCanvas(screenshot, activeTool, color) {
  const { t } = useTranslation();
  const canvasElRef = useRef(null);
  const fabricRef = useRef(null);
  const cropRectRef = useRef(null);
  const undoSnapshotRef = useRef(null);
  const nativeWidthRef = useRef(null);
  const [hasPendingCrop, setHasPendingCrop] = useState(false);
  const [canUndo, setCanUndo] = useState(false);

  // (Re)create the canvas whenever a new screenshot is loaded.
  useEffect(() => {
    if (!screenshot || !canvasElRef.current) return;
    let cancelled = false;
    cropRectRef.current = null;
    setHasPendingCrop(false);
    undoSnapshotRef.current = null;
    setCanUndo(false);
    fabric.Image.fromURL(screenshot).then((img) => {
      if (cancelled) return;
      // Fit to the actual available width on screen (mobile viewports are far
      // narrower than the desktop-oriented 880px cap), so the full image and
      // toolbar are visible without needing to scroll horizontally to see it.
      const availableWidth = canvasElRef.current?.parentElement?.clientWidth || MAX_CANVAS_WIDTH;
      const effectiveMaxWidth = Math.min(MAX_CANVAS_WIDTH, availableWidth);
      const scale = Math.min(1, effectiveMaxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = new fabric.Canvas(canvasElRef.current, { width: w, height: h });
      img.set({ left: 0, top: 0, originX: "left", originY: "top", scaleX: w / img.width, scaleY: h / img.height, selectable: false, evented: false, isBackground: true });
      canvas.add(img);
      canvas.renderAll();
      // Remember the source image's native width so exports can be upscaled
      // back to it - the on-screen canvas may be rendered much smaller than
      // the original screenshot (e.g. on a narrow phone), and exporting at
      // that shrunk resolution would permanently degrade the saved image.
      nativeWidthRef.current = img.width;
      fabricRef.current = canvas;
    });
    return () => {
      cancelled = true;
      fabricRef.current?.dispose();
      fabricRef.current = null;
    };
  }, [screenshot]);

  // Wire up the active tool's mouse interactions.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = activeTool === "pen";
    if (activeTool === "pen") {
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = color;
      canvas.freeDrawingBrush.width = FREE_DRAW_STROKE_WIDTH;
    }
    canvas.selection = activeTool === "select";
    canvas.forEachObject((o) => {
      if (o.get("isBackground") || o.get("isCropMarker")) return;
      o.selectable = activeTool === "select";
      o.evented = activeTool === "select";
    });

    let shape = null;
    let startX = 0;
    let startY = 0;
    let erasing = false;

    // Captures the canvas state right before a mutating action so a single
    // undo can restore it. Only one level of history is kept (per product
    // decision), so each new action simply overwrites the previous snapshot.
    const snapshotBeforeAction = () => {
      undoSnapshotRef.current = JSON.stringify(canvas.toJSON(["isBackground", "isCropMarker"]));
      setCanUndo(true);
    };

    // Manual topmost-first hit-test using each object's live bounding box
    // (fabric's containsPoint relies on cached corner coords which can be
    // stale for objects that were just resized during a drag).
    const findEraseTarget = (pointer) => {
      const objects = canvas.getObjects();
      for (let i = objects.length - 1; i >= 0; i--) {
        const obj = objects[i];
        if (obj.get("isBackground") || obj.get("isCropMarker")) continue;
        obj.setCoords();
        const rect = obj.getBoundingRect();
        if (pointer.x >= rect.left && pointer.x <= rect.left + rect.width && pointer.y >= rect.top && pointer.y <= rect.top + rect.height) {
          return obj;
        }
      }
      return null;
    };

    const eraseAt = (pointer) => {
      const target = findEraseTarget(pointer);
      if (target) {
        canvas.remove(target);
        canvas.requestRenderAll();
      }
    };

    const onMouseDown = (opt) => {
      const pointer = canvas.getScenePoint(opt.e);
      if (activeTool === "eraser") {
        snapshotBeforeAction();
        erasing = true;
        eraseAt(pointer);
        return;
      }
      if (activeTool === "text") {
        // If the click landed on an existing text object, re-enter editing on
        // it instead of stacking a brand new textbox on top (this is what
        // made double-clicking existing text appear broken).
        const existing = findEraseTarget(pointer);
        snapshotBeforeAction();
        if (existing && existing.type === "textbox") {
          canvas.setActiveObject(existing);
          existing.enterEditing();
          existing.selectAll();
          canvas.requestRenderAll();
          return;
        }
        const text = new fabric.Textbox(t("editor.newTextPlaceholder"), { left: pointer.x, top: pointer.y, originX: "left", originY: "top", fill: color, fontSize: 20, fontFamily: "IBM Plex Sans" });
        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        text.selectAll();
        canvas.requestRenderAll();
        return;
      }
      if (!DRAG_SHAPE_TOOLS.includes(activeTool)) {
        // "select" tool: about to move an existing object under the cursor.
        // "pen": about to start a new freehand stroke (handled internally by
        // Fabric's PencilBrush, but still worth snapshotting for undo).
        if (activeTool === "select" && opt.target) snapshotBeforeAction();
        else if (activeTool === "pen") snapshotBeforeAction();
        return;
      }
      if (activeTool !== "crop") snapshotBeforeAction();
      startX = pointer.x;
      startY = pointer.y;
      if (activeTool === "rect") {
        shape = new fabric.Rect({ left: startX, top: startY, originX: "left", originY: "top", width: 1, height: 1, fill: "transparent", stroke: color, strokeWidth: SHAPE_STROKE_WIDTH, selectable: false });
      } else if (activeTool === "highlight") {
        shape = new fabric.Rect({ left: startX, top: startY, originX: "left", originY: "top", width: 1, height: 1, fill: color, opacity: 0.35, selectable: false });
      } else if (activeTool === "circle") {
        shape = new fabric.Ellipse({ left: startX, top: startY, originX: "left", originY: "top", rx: 1, ry: 1, fill: "transparent", stroke: color, strokeWidth: SHAPE_STROKE_WIDTH, selectable: false });
      } else if (activeTool === "crop") {
        if (cropRectRef.current) {
          canvas.remove(cropRectRef.current);
          cropRectRef.current = null;
          setHasPendingCrop(false);
        }
        shape = new fabric.Rect({ left: startX, top: startY, originX: "left", originY: "top", width: 1, height: 1, fill: "rgba(0,0,0,0.15)", stroke: "#ffffff", strokeDashArray: [6, 4], strokeWidth: 2, selectable: false, isCropMarker: true });
      } else if (activeTool === "arrow" || activeTool === "line") {
        shape = new fabric.Line([startX, startY, startX, startY], { stroke: color, strokeWidth: SHAPE_STROKE_WIDTH, selectable: false });
      }
      canvas.add(shape);
    };

    const onMouseMove = (opt) => {
      if (activeTool === "eraser") {
        if (erasing) eraseAt(canvas.getScenePoint(opt.e));
        return;
      }
      if (!shape) return;
      const pointer = canvas.getScenePoint(opt.e);
      if (activeTool === "rect" || activeTool === "highlight" || activeTool === "crop") {
        shape.set({ width: Math.abs(pointer.x - startX), height: Math.abs(pointer.y - startY), left: Math.min(pointer.x, startX), top: Math.min(pointer.y, startY) });
      } else if (activeTool === "circle") {
        shape.set({ rx: Math.abs(pointer.x - startX) / 2, ry: Math.abs(pointer.y - startY) / 2, left: Math.min(pointer.x, startX), top: Math.min(pointer.y, startY) });
      } else if (activeTool === "arrow" || activeTool === "line") {
        canvas.remove(shape);
        shape = new fabric.Line([startX, startY, pointer.x, pointer.y], { stroke: color, strokeWidth: SHAPE_STROKE_WIDTH, selectable: false });
        canvas.add(shape);
      }
      // Fabric caches each object's hit-test corners (oCoords/aCoords) and does
      // NOT recompute them automatically after .set() resizes width/height/rx/ry.
      // Without this, the object's clickable/selectable area stays stuck at its
      // tiny 1x1 creation size, so it can never be selected or dragged later.
      shape.setCoords();
      canvas.requestRenderAll();
    };

    const onMouseUp = () => {
      if (activeTool === "eraser") {
        erasing = false;
        return;
      }
      if (shape && activeTool === "arrow") {
        const dx = shape.x2 - shape.x1;
        const dy = shape.y2 - shape.y1;
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
        const triangle = new fabric.Triangle({ left: shape.x2, top: shape.y2, originX: "center", originY: "center", angle, width: ARROWHEAD_SIZE, height: ARROWHEAD_SIZE, fill: color });
        // Group the shaft + arrowhead into one object so select/erase always
        // treat the whole arrow as a single unit (avoids leaving an orphaned
        // triangle behind when only the shaft is under the eraser).
        canvas.remove(shape);
        shape = new fabric.Group([shape, triangle], { selectable: false });
        canvas.add(shape);
      }
      if (shape && activeTool === "crop") {
        cropRectRef.current = shape;
        setHasPendingCrop(true);
      } else if (shape) {
        shape.set({ selectable: activeTool === "select" });
        shape.setCoords();
      }
      shape = null;
      canvas.renderAll();
    };

    canvas.on("mouse:down", onMouseDown);
    canvas.on("mouse:move", onMouseMove);
    canvas.on("mouse:up", onMouseUp);
    return () => {
      canvas.off("mouse:down", onMouseDown);
      canvas.off("mouse:move", onMouseMove);
      canvas.off("mouse:up", onMouseUp);
    };
  }, [activeTool, color, t]);

  const clearAnnotations = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    undoSnapshotRef.current = JSON.stringify(canvas.toJSON(["isBackground", "isCropMarker"]));
    setCanUndo(true);
    canvas.getObjects().slice(1).forEach((o) => canvas.remove(o));
    cropRectRef.current = null;
    setHasPendingCrop(false);
    canvas.renderAll();
  };

  // Restores the single saved pre-action snapshot (per product decision, only
  // one undo level is kept - no redo, no multi-step history).
  const undo = () => {
    const canvas = fabricRef.current;
    if (!canvas || !undoSnapshotRef.current) return;
    canvas.loadFromJSON(undoSnapshotRef.current).then(() => {
      canvas.forEachObject((o) => {
        if (o.get("isBackground") || o.get("isCropMarker")) return;
        o.selectable = activeTool === "select";
        o.evented = activeTool === "select";
        o.setCoords();
      });
      canvas.renderAll();
    });
    undoSnapshotRef.current = null;
    setCanUndo(false);
  };

  // Confirms the pending crop selection: exports only that region (background +
  // annotations flattened together) and returns it as a data URL. The caller
  // is expected to feed this back in as the new screenshot, which resets the
  // canvas with the cropped image as the fresh base.
  const confirmCrop = () => {
    const canvas = fabricRef.current;
    const rect = cropRectRef.current;
    if (!canvas || !rect) return null;
    const { left, top, width, height } = rect;
    canvas.remove(rect);
    cropRectRef.current = null;
    setHasPendingCrop(false);
    canvas.renderAll();
    if (width < 2 || height < 2) return null;
    return canvas.toDataURL({ format: "png", left, top, width, height, multiplier: getExportMultiplier() });
  };

  const cancelCrop = () => {
    const canvas = fabricRef.current;
    if (cropRectRef.current) {
      canvas?.remove(cropRectRef.current);
      cropRectRef.current = null;
    }
    setHasPendingCrop(false);
    canvas?.renderAll();
  };

  // The on-screen canvas is often rendered smaller than the original
  // screenshot (e.g. shrunk to fit a narrow phone viewport), so exports must
  // be scaled back up to the source image's native resolution - otherwise
  // saving from a small screen would permanently degrade the stored image
  // quality even when later viewed on a larger screen.
  const getExportMultiplier = () => {
    const canvas = fabricRef.current;
    if (!canvas || !nativeWidthRef.current) return 1;
    return nativeWidthRef.current / canvas.getWidth();
  };

  const exportDataUrl = () => fabricRef.current?.toDataURL({ format: "png", multiplier: getExportMultiplier() });

  return { canvasElRef, clearAnnotations, exportDataUrl, hasPendingCrop, confirmCrop, cancelCrop, undo, canUndo };
}
